import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter,
} from "@/components/ui/alert-dialog";

// Warns before leaving with unsaved changes: browser prompt on close/refresh, dialog on in-app links.
export function UnsavedChangesGuard({ count, saving, onSave }) {
  const navigate = useNavigate();
  const [to, setTo] = useState(null);

  useEffect(() => {
    if (!count) return;
    const onUnload = (e) => { e.preventDefault(); e.returnValue = ""; };
    const onClick = (e) => {
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const a = e.target.closest?.("a[href]");
      if (!a || a.target === "_blank" || a.hasAttribute("download")) return;
      const url = new URL(a.href, window.location.href);
      if (url.origin !== window.location.origin || url.pathname === window.location.pathname) return;
      e.preventDefault();
      e.stopPropagation();
      setTo(url.pathname + url.search + url.hash);
    };
    window.addEventListener("beforeunload", onUnload);
    document.addEventListener("click", onClick, true);
    return () => {
      window.removeEventListener("beforeunload", onUnload);
      document.removeEventListener("click", onClick, true);
    };
  }, [count]);

  const leave = () => { const dest = to; setTo(null); navigate(dest); };
  const saveAndLeave = async () => { if (await onSave()) leave(); else setTo(null); };

  return (
    <AlertDialog open={!!to} onOpenChange={(o) => !o && setTo(null)}>
      <AlertDialogContent data-testid="unsaved-changes-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>You have unsaved changes</AlertDialogTitle>
          <AlertDialogDescription>
            {count === 1 ? "1 change" : `${count} changes`} to your profile will be lost if you leave now.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter className="gap-2">
          <button type="button" onClick={() => setTo(null)} data-testid="unsaved-stay"
                  className="rounded-md border border-stone-300 px-4 py-2 text-sm text-stone-700 hover:bg-stone-50">
            Stay on page
          </button>
          <button type="button" onClick={leave} data-testid="unsaved-leave"
                  className="rounded-md border border-red-200 px-4 py-2 text-sm text-red-700 hover:bg-red-50">
            Leave without saving
          </button>
          <button type="button" onClick={saveAndLeave} disabled={saving} data-testid="unsaved-save-leave"
                  className="rounded-md bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-60">
            {saving ? "Saving…" : "Save and leave"}
          </button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
